<script module>
  import { setTag, init, graphqlClientIntegration } from '@sentry/browser'

  import { createClient as createPrismicClient } from '@prismicio/client'
  import { setLanguage, setCurrency } from '$lib/context.js'
  import State from '$lib/utils/state.svelte.js'
  import { gql } from '$lib/graphql.svelte.js'
  import styles from '$lib/main.css?inline'
  import Config from '$lib/Config.svelte'

  const CSS_URL_PATTERN = /url\((?:'|")?([^'"]+?)\1?\)/g

  const { origin } = new URL(import.meta.url)
  const css = styles
    .replace(
      CSS_URL_PATTERN,
      (_, path) =>
        `url(${path.startsWith('data:') ? path : new URL(path, origin)})`
    )
    .replaceAll(':root', ':host')

  const prismicClient = createPrismicClient('allaboard')

  /**
   * @param {string} name
   * @param {Schema.SalesAgent} agent
   * @param {{ url: string|URL, referrer?: string, props?: Object<string, any>, revenue?: Schema.Money }} params
   */
  export function track(name, agent, params) {
    const url = new URL(params.url, origin)
    if (!url.searchParams.has('utm_source')) {
      url.searchParams.set('utm_source', agent.uid)
    }

    const referrer = window.location.href
    const domain = `allaboard.eu,${agent.uid}.allaboard.eu`
    return fetch(new URL('/stats/event', origin), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ referrer, ...params, url, name, domain })
    })
  }

  export function getSessionId() {
    const match = document.cookie.match(/(?:^|;\s*)aa.session=(.+?)(?:;|$)/)
    if (match) return match[1]

    const sessionId =
      typeof globalThis !== 'undefined' &&
      typeof globalThis?.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : Math.random().toString(36).substring(2)

    document.cookie = `aa.session=${sessionId}; path=/; samesite=lax; secure`

    return sessionId
  }
</script>

<script>
  /** @type {{
    children: Svelte.Snippet,
    error?: Svelte.Snippet,
    name: string,
    agent?: Schema.SalesAgent,
    settings?: Prismic.PrismicDocument,
    sessionId?: string,
    language?: string,
    currency?: string
  }} */
  let {
    error,
    children,
    name,
    agent = $bindable(),
    settings = $bindable(),
    sessionId = getSessionId(),
    language = State.language,
    currency = State.currency
  } = $props()

  const url = new URL(import.meta.url)

  const websocket = new URL('/api', url.origin)
  websocket.protocol = websocket.protocol.replace(/^http/, 'ws')

  init({
    sendDefaultPii: true,
    tracesSampleRate: 1.0,
    allowUrls: [url.origin],
    dsn: import.meta.env.PUBLIC_SENTRY_DSN,
    environment: import.meta.env.PUBLIC_SENTRY_ENVIRONMENT,
    integrations: [
      graphqlClientIntegration({
        endpoints: [
          String(websocket),
          String(new URL('/api', url.origin)),
          import.meta.env.PUBLIC_API_GATEWAY_ORIGIN,
          String(new URL('/api', import.meta.env.PUBLIC_API_INTERNAL_ORIGIN))
        ]
      })
    ]
  })

  $effect(() => {
    setTag('embed', name)
    setTag('session', sessionId)
    State.language = language
    State.currency = currency
    State.origin = origin
    setLanguage(language)
    setCurrency(currency)
  })

  /** @type {App.GraphQLOperation<{ agent: Schema.SalesAgent }>}*/
  const GetAgent = gql`
    query GetAgent {
      agent: getSalesAgent {
        uid
      }
    }
  `

  GetAgent.execute()

  $effect(() => {
    agent = GetAgent.data?.agent
  })

  $effect(() => {
    if (GetAgent.data?.agent) {
      prismicClient.getByUID('agent', GetAgent.data.agent.uid).then((doc) => {
        settings = doc
      })
    }
  })
</script>

{@html `<style>${css}</style>`}

<Config
  primary={settings?.data.primary_color}
  secondary={settings?.data.secondary_color}>
  {#if GetAgent.error}
    {@render error?.()}
  {:else}
    {@render children()}
  {/if}
</Config>

<style>
  :host {
    display: contents;
  }
</style>
